import { useState } from "react";
import { useMutation } from "convex/react";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { api } from "../../../convex/_generated/api";
import { Loader2 } from "lucide-react";
import type { Id } from "../../../convex/_generated/dataModel";

export default function NewEvent() {
  const navigate = useNavigate();
  const createEvent = useMutation(api.events.create);
  const getOrCreateUser = useMutation(api.users.getOrCreate);

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [date, setDate] = useState("");
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function slugify(text: string) {
    return text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  }

  function handleNameChange(val: string) {
    setName(val);
    setSlug(slugify(val));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // M-3: email wajib dari identity, bukan input manual.
      // Fallback "host@fotomomen.test" hanya untuk dev mode tanpa auth.
      const hostEmail = email || (import.meta.env.DEV ? "host@fotomomen.test" : "");
      if (!hostEmail) {
        setError("Email wajib diisi untuk membuat event.");
        setLoading(false);
        return;
      }
      const hostId = (await getOrCreateUser({
        email: hostEmail,
        name: "Host",
        role: "host",
      })) as Id<"users">;

      await createEvent({
        name,
        slug,
        hostId,
        date: date || new Date().toISOString(),
        location,
        description: description || undefined,
      });

      navigate(`/dashboard/${slug}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Gagal membuat event");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-2 mb-6">
          <span className="text-xl">📸</span>
          <span className="font-bold text-lg text-slate-900">FotoMomen</span>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Buat Event Baru</CardTitle>
            <p className="text-sm text-slate-500">Isi detail event yang akan dijalankan</p>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
                  {error}
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Nama Event *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  required
                  placeholder="Pernikahan Budi & Ani"
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[40px]"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Slug URL (otomatis)</label>
                <div className="flex items-center rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-500">
                  <span className="text-slate-400 mr-1">/checkin/</span>
                  <input
                    type="text"
                    value={slug}
                    onChange={(e) => setSlug(slugify(e.target.value))}
                    required
                    className="flex-1 bg-transparent focus:outline-none text-slate-900"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Tanggal *</label>
                  <input
                    type="datetime-local"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    required
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[40px]"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Lokasi *</label>
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    required
                    placeholder="Gedung Serba Guna XYZ"
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[40px]"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Email Host (opsional)</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="host@email.com"
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[40px]"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Deskripsi (opsional)</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="Deskripsi singkat tentang event..."
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
            </CardContent>

            <CardFooter className="flex gap-3">
              <Button type="submit" disabled={loading || !name || !slug || !location} className="flex-1 gap-2">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {loading ? "Membuat Event..." : "Buat Event"}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate("/")}>
                Batal
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
}